import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Minimal Markdown-to-HTML pipe for Copilot messages.
 * Handles: **bold**, *italic*, `code`, headers (##), bullet lists, line breaks.
 * Does NOT use a full Markdown library to avoid adding dependencies.
 */
@Pipe({ name: 'copilotMarkdown', standalone: false })
export class CopilotMarkdownPipe implements PipeTransform {

  constructor(private sanitizer: DomSanitizer) {}

  transform(text: string): SafeHtml {
    if (!text) return '';

    // Stash already-decided HTML fragments (quoted config values) behind
    // letter-only placeholders so later passes never repaint a config value's
    // own digits from white to orange.
    const stash: string[] = [];
    const alphabet = 'abcdefghijklmnopqrstuvwxyz';
    const encodeIdx = (i: number): string => {
      if (i === 0) return alphabet[0];
      let s = '';
      while (i > 0) {
        s = alphabet[i % 26] + s;
        i = Math.floor(i / 26);
      }
      return s;
    };
    const decodeIdx = (s: string): number => {
      let i = 0;
      for (const ch of s) i = i * 26 + alphabet.indexOf(ch);
      return i;
    };
    const stow = (fragment: string): string => {
      stash.push(fragment);
      return `\x00${encodeIdx(stash.length - 1)}\x00`;
    };

    let html = text
      // Escape HTML
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Literal config values (rule anchor strings, Find & Replace keys/values, etc.)
      // the LLM wraps in double quotes per its prompt instruction — white, distinct
      // from body text and from the single-quoted OUTPUT-value examples elsewhere in
      // the same narration. Stashed so the stat-number pass below never repaints a
      // numeric anchor's digits from white to orange.
      .replace(/"([^"\n]{1,200})"/g,
        (_m, inner) => stow(`<span style="color:#ffffff">"${inner}"</span>`)
      )
      // DTR Copilot Config Analyser: the deterministic Composite/Quality/Accuracy/
      // Reliability(/Convergence) Scorecard line (checkpoint.py inserts it as its
      // own \n\n-joined part, before the LLM's discussion) — orange, matching the
      // Description field's styling for the same line in session_summary.py's
      // _HEADER_LABEL_COLOR/_SCORES_LINE_RE. Matches "Composite X.XX" followed by
      // one or more "· Label X.XX" terms rather than a hardcoded 4-term literal,
      // so an added term (e.g. Convergence) doesn't require touching this regex.
      // Forces a paragraph break on both sides too (mirrors session_summary.py's
      // _isolate_scores_paragraph) so the line reads standalone even if it ever
      // arrives glued to adjacent text with no separator. Runs BEFORE the general
      // stat-number pass below, which needs this line's raw digits to still be
      // unwrapped when IT runs — reversing the order would let this regex's own
      // [\d.]+ try to match across already-inserted <span> tags and fail.
      .replace(/Composite\s+[\d.]+(?:\s*·\s*[A-Za-z][A-Za-z ]*\s+[\d.]+)+\.?/g,
        (m) => `\n\n<span style="color:#ff9f0c">${m}</span>\n\n`
      )
      // Deterministic stat phrases matching the Results Dashboard's own tracked
      // tiles — Hit Rate and Farm Rows specifically (Architect's explicit list,
      // 2026-08-30: "orange is reserved for statistics that match those seen in
      // the Results Dashboard... Hit Rate Ratio, Farm Rows, Misses, Variants, DTR
      // Copilot Scores"). Deliberately narrow — matches the EXACT phrasing
      // format_checkpoint_stats() / _get_row_removal_stat() construct in
      // checkpoint.py, not any bare number. A prior version colored every
      // digit-like token in the narrative (rule counts, tier breakdowns, timing
      // figures, sample ranges) — confirmed "over-oranged" and reverted. Misses
      // and Variants aren't covered here yet — no deterministic chat-narration
      // constructor for them was found; extend this the same way once one exists.
      .replace(/\d+(?:\.\d+)?%\s+hit rate\s*\([\d,]+(?:\s+of\s+[\d,]+)?\s+rows?\)/g,
        (m) => `<span style="color:#ff9f0c">${m}</span>`
      )
      .replace(/[\d,]+\s+of\s+[\d,]+\s+rows retained\s*\([\d,]+\s+removed\)/g,
        (m) => `<span style="color:#ff9f0c">${m}</span>`
      )
      // Run Time (uncompiled)/(compiled) — matches checkpoint.py's phrase reporting
      // compute_config_analysis's real full-farm execution time through each of
      // DTR's two real engines. Compiled half always appears but may read "NA"
      // (config not fully compilable, or its own Run All Rows is off) rather than
      // a number — matched either way as one combined phrase.
      .replace(/Run Time \(uncompiled\):\s*\d+(?:\.\d+)?s\s+Run Time \(compiled\):\s*(?:\d+(?:\.\d+)?s|NA)/g,
        (m) => `<span style="color:#ff9f0c">${m}</span>`
      )
      // [? About] button reference — styled to match the actual UI button
      .replace(/\[\? About\]/g,
        '<strong><span style="color:#42a5f5">?</span>' +
        '&nbsp;<span style="color:#ffffff">About</span></strong>'
      )
      // Find Phrases UI locations — colored to match DTR Start Rule / End Rule panel labels
      .replace(/\(Start Rule - Offset\)/g,
        '<strong><span style="color:#9AFF00">(Start Rule - Offset)</span></strong>'
      )
      .replace(/\(End Rule - Offset\/Length\)/g,
        '<strong><span style="color:#FF9F0C">(End Rule - Offset/Length)</span></strong>'
      )
      // Headers
      .replace(/^### (.+)$/gm, '<h4>$1</h4>')
      .replace(/^## (.+)$/gm,  '<h3>$1</h3>')
      .replace(/^# (.+)$/gm,   '<h2>$1</h2>')
      // Bold and italic
      .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      // Handle **Label**: (colon outside closing markers) — pull colon into the strong tag
      .replace(/\*\*(.+?)\*\*:/g,    '<strong>$1:</strong>')
      .replace(/\*\*(.+?)\*\*/g,     '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g,         '<em>$1</em>')
      // Inline code
      .replace(/`([^`]+)`/g, '<code class="copilot-code">$1</code>')
      // Bullet lists
      .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
      // Horizontal rule
      .replace(/^---$/gm, '<hr>')
      // Paragraph breaks (2+ newlines) become a visible spacer (inline style avoids Angular scoping)
      .replace(/\n{2,}/g, '<div style="height:0.5em;display:block"></div>')
      // Convert all remaining newlines to line breaks
      .replace(/\n/g, '<br>')
      // Block-level elements already force a line break — remove the redundant <br>
      // that follows them (produced by the \n after a heading/list/hr in LLM output)
      .replace(/(<\/(?:h[2-6]|ul|ol)>|<hr>)<br>/g, '$1')
      // Remove trailing <br> inside <ul> from the \n after the last <li>
      .replace(/<br>(<\/ul>)/g, '$1')
      // Restore stashed fragments (quoted config values, protected rule refs) now
      // that no further regex in this chain will touch their contents.
      .replace(/\x00([a-z]+)\x00/g, (_m, idx) => stash[decodeIdx(idx)]);

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
